import FilterSelect, {type FilterSelectOption} from 'common/components/FilterSelect'
import ClassGroupPicker from 'common/components/dataUpload/ClassGroupPicker'
import FilePreviewModal, {
  canPreviewFile,
  findUploadedFile,
} from 'common/components/dataUpload/FilePreviewModal'
import {
  type DataUploadAllocationAssignment,
  type DataUploadAllocationQuestion,
  type DataUploadBranchAssignments,
  type DataUploadBranchQuestion,
  type DataUploadBranchRowNoun,
  type DataUploadColumnAssignments,
  type DataUploadColumnQuestion,
  type DataUploadColumnQuestions,
  type DataUploadNewTeacher,
  type DataUploadSubjectMapping,
  type DataUploadSubjectMappings,
  IGNORE_COLUMN,
  MIXED_BRANCH,
  SUBJECT_MAPPING_CONFIRM_THRESHOLD,
  allocationAnswerKey,
  defaultConfirmedSubjectMappings,
  isFileBranchQuestion,
  skippedAllocationAnswers,
} from 'common/helpers/dataUpload'
import {
  AlertTriangle,
  BookOpen,
  Building2,
  Check,
  Columns3,
  Eye,
  FileText,
  Info,
  UserPlus,
  UserRound,
} from 'lucide-react'
import React, {FC, useEffect, useId, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'

/** Everything the user answered, sent with ONE re-read of the same files. */
export interface DataUploadQuestionAnswers {
  /** Column header -> field; only the columns asked in this dialog. */
  columns: DataUploadColumnAssignments
  /** Branch question key -> `branchId` or `MIXED_BRANCH`; only the sheets asked in this dialog. */
  branches: DataUploadBranchAssignments
  /** Ticked subject proposals, ready for `POST data-upload/subject-aliases`. Never an unticked one. */
  subjects: DataUploadSubjectMapping[]
  /**
   * Every asked teacher + subject with the classes picked for it. One left empty is sent with no
   * class: not allocated, and not asked about again.
   */
  allocations: DataUploadAllocationAssignment[]
  /**
   * The `key`s of the new teachers ticked to save, exactly as the server sent them
   * (`approvedNewTeachers`). The unticked ones are declined.
   */
  newTeachers: string[]
  /**
   * "Save teachers / students only" was chosen (`peopleOnly`): no class or subject answers are
   * sent (`allocations` and `subjects` are empty), and the caller stops asking them.
   */
  peopleOnly: boolean
}

/**
 * What "Skip" answers, for every question in the dialog. Columns and teacher + subject lines have
 * a skip value the server keeps (`ignore`; no classes: not allocated, never asked again); a branch
 * has none, so the caller remembers `branches` (and `subjects`, the proposals' written names) itself
 * so a re-read does not ask them again.
 */
export interface DataUploadSkipAnswers {
  /** Every asked column header -> `IGNORE_COLUMN`. */
  columns: DataUploadColumnAssignments
  /** The branch question keys skipped. */
  branches: string[]
  /** The subject proposals skipped, by `written`. */
  subjects: string[]
  /** Every asked teacher + subject with no class. */
  allocations: DataUploadAllocationAssignment[]
}

/** Who the "save people only" choice is about. */
export type DataUploadPeopleOnlyNoun = 'teachers' | 'students'

/** The two choices' wording: "Save … only", and "Also choose …". */
const PEOPLE_ONLY_WORDING = {
  teachers: {
    only: {
      title: 'DATA_UPLOAD.PEOPLE_ONLY.TEACHERS_ONLY',
      hint: 'DATA_UPLOAD.PEOPLE_ONLY.TEACHERS_ONLY_HINT',
    },
    also: {
      title: 'DATA_UPLOAD.PEOPLE_ONLY.TEACHERS_ALSO',
      hint: 'DATA_UPLOAD.PEOPLE_ONLY.TEACHERS_ALSO_HINT',
    },
  },
  students: {
    only: {
      title: 'DATA_UPLOAD.PEOPLE_ONLY.STUDENTS_ONLY',
      hint: 'DATA_UPLOAD.PEOPLE_ONLY.STUDENTS_ONLY_HINT',
    },
    also: {
      title: 'DATA_UPLOAD.PEOPLE_ONLY.STUDENTS_ALSO',
      hint: 'DATA_UPLOAD.PEOPLE_ONLY.STUDENTS_ALSO_HINT',
    },
  },
} satisfies Record<
  DataUploadPeopleOnlyNoun,
  Record<'only' | 'also', Record<'title' | 'hint', string>>
>

interface Props {
  show: boolean
  /** The columns the server could not place for sure, and the fields it offers. */
  columns: DataUploadColumnQuestions | null
  /** "Which branch are these students in?", one per sheet. */
  branches: readonly DataUploadBranchQuestion[]
  /**
   * Answers the branch cards start from: the ones given earlier, when the user reopens the
   * dialog to change them ("mixed" instead of a branch). Nothing is picked otherwise.
   */
  initialBranches?: DataUploadBranchAssignments
  /** Subjects the file writes under a name the school does not use. */
  subjects: DataUploadSubjectMappings | null
  /** Teachers step: a teacher's subjects with no class — which classes each one is taught in. */
  allocations?: readonly DataUploadAllocationQuestion[]
  /**
   * Teachers the asked lines name that the school has not saved (Teacher Allocation). Each starts
   * ticked; the ticked ones are saved with the answers, the others' lines are left out.
   */
  newTeachers?: readonly DataUploadNewTeacher[]
  /**
   * Offer "Save teachers / students only" (chosen to begin with) or "Also choose classes": the
   * people are saved either way, and only the second shows the class (and subject) questions.
   */
  peopleOnly?: DataUploadPeopleOnlyNoun
  /** Busy while the answers are saved and the files are read again. */
  submitting?: boolean
  onConfirm: (answers: DataUploadQuestionAnswers) => void
  /**
   * The footer's Skip: answers every question with its skip value (`skipped`), which the caller
   * sends once so the questions are not asked again.
   */
  onSkip: (skipped: DataUploadSkipAnswers) => void
  /**
   * The X (and Esc / backdrop): closes without answering. The questions stay pending, with a way
   * back to them; nothing is sent. Without it, the X skips (older callers).
   */
  onClose?: () => void
  /** The uploaded files, so the file a column or sheet is in can be opened to look at it. */
  files?: readonly File[]
  /**
   * "Which classes?" as a review of the file's proposals: a subject the server proposed classes
   * for (`picked`, or `fromFile`) shows them as chips with how the file was read (`reading`) and
   * an Edit that opens the picker; only the unresolved ones start with the picker open. The
   * footer button says "Confirm assignments". Off (older callers): every picker shows at once.
   */
  proposalReview?: boolean
}

/** A column's guess, when it has one: the answer the dialog starts from. */
const guessOf = (question: DataUploadColumnQuestion) => question.guess?.field ?? ''

const percent = (confidence: number | null | undefined) =>
  typeof confidence === 'number' ? `${Math.round(confidence * 100)}%` : ''

/**
 * The branch question's wording by who the rows are (`rowNoun`). Teachers-step sheets are asked
 * too (a staff sheet with classes is also the allocation), so "students" is not always right.
 */
const BRANCH_WORDING = {
  student: {
    title: 'DATA_UPLOAD.BRANCHES.TITLE',
    hint: 'DATA_UPLOAD.BRANCHES.HINT',
    rows: 'DATA_UPLOAD.BRANCHES.ROWS',
    question: 'DATA_UPLOAD.BRANCHES.QUESTION',
  },
  teacher: {
    title: 'DATA_UPLOAD.BRANCHES.TITLE_TEACHER',
    hint: 'DATA_UPLOAD.BRANCHES.HINT_TEACHER',
    rows: 'DATA_UPLOAD.BRANCHES.ROWS_TEACHER',
    question: 'DATA_UPLOAD.BRANCHES.QUESTION_TEACHER',
  },
  allocation: {
    title: 'DATA_UPLOAD.BRANCHES.TITLE_ALLOCATION',
    hint: 'DATA_UPLOAD.BRANCHES.HINT_ALLOCATION',
    rows: 'DATA_UPLOAD.BRANCHES.ROWS_ALLOCATION',
    question: 'DATA_UPLOAD.BRANCHES.QUESTION_ALLOCATION',
  },
} satisfies Record<DataUploadBranchRowNoun, Record<'title' | 'hint' | 'rows' | 'question', string>>

/** Without `rowNoun` (an older backend or draft) the rows are students, as before. */
const nounOf = (question: DataUploadBranchQuestion): DataUploadBranchRowNoun =>
  question.rowNoun ?? 'student'

/** The issues a file's "Which classes?" lines are counted by, under its heading. */
const ALLOCATION_ISSUE_KEYS = {
  NO_CLASS_WRITTEN: 'DATA_UPLOAD.ALLOCATIONS.ISSUE.NO_CLASS_WRITTEN',
  CLASS_UNCLEAR: 'DATA_UPLOAD.ALLOCATIONS.ISSUE.CLASS_UNCLEAR',
  SUBJECT_NOT_IN_SCHOOL: 'DATA_UPLOAD.ALLOCATIONS.ISSUE.SUBJECT_NOT_IN_SCHOOL',
  CLASS_NOT_NAMED: 'DATA_UPLOAD.ALLOCATIONS.ISSUE.CLASS_NOT_NAMED',
  CLASS_NOT_IN_GRADE: 'DATA_UPLOAD.ALLOCATIONS.ISSUE.CLASS_NOT_IN_GRADE',
  SUBJECT_NOT_IN_GRADE: 'DATA_UPLOAD.ALLOCATIONS.ISSUE.SUBJECT_NOT_IN_GRADE',
} as const
type AllocationIssueKey = keyof typeof ALLOCATION_ISSUE_KEYS

/** No allocation question: one list, not a new one per render. */
const NO_ALLOCATION_QUESTIONS: DataUploadAllocationQuestion[] = []

/** No new teacher: one list, not a new one per render. */
const NO_NEW_TEACHERS: DataUploadNewTeacher[] = []

/** Sample rows shown per branch question (the server sends up to 3). */
const MAX_BRANCH_SAMPLE_ROWS = 3

/**
 * The classes a subject starts ticked with, among those the dialog shows: the ones the file names
 * (`picked`: "both sections" is A and B), else all of them when they are the file's own classes
 * (`fromFile`), else none (the line is unresolved).
 */
const proposedClassIds = (entry: DataUploadAllocationQuestion['subjects'][number]): number[] => {
  const offered = entry.options.map((option) => option.classId)
  if (entry.picked) return offered.filter((classId) => entry.picked!.includes(classId))
  return entry.fromFile ? offered : []
}

/** Two class-id lists hold the same classes. */
const sameClassIds = (a: readonly number[], b: readonly number[]) =>
  a.length === b.length && a.every((classId) => b.includes(classId))

/**
 * The questions a read asks once its rows are in the grid, in one dialog: "which field is this
 * column?", "which branch are these students in?" and "is this the same subject?".
 *
 * The rows are already read, so skipping costs nothing. Confirm saves the ticked subject
 * mappings (the caller posts them to `data-upload/subject-aliases`) and reads the same files
 * again ONCE with every column and branch answer. Column answers and subject mappings are saved
 * for the school; branch answers belong to this upload and ride along with every later read.
 *
 * Every column and every branch question needs an answer before Confirm: one left out would be
 * asked again and the dialog would come straight back. A subject proposal is optional: a
 * confident one starts ticked, a doubtful one does not, and an unticked one is never sent.
 */
const ColumnAssignmentModal: FC<Props> = ({
  show,
  columns,
  branches,
  initialBranches,
  subjects,
  allocations = NO_ALLOCATION_QUESTIONS,
  newTeachers = NO_NEW_TEACHERS,
  peopleOnly,
  submitting = false,
  onConfirm,
  onSkip,
  onClose,
  files = [],
  proposalReview = false,
}) => {
  const intl = useIntl()
  const fieldId = useId()
  const [columnAnswers, setColumnAnswers] = useState<DataUploadColumnAssignments>({})
  const [branchAnswers, setBranchAnswers] = useState<DataUploadBranchAssignments>({})
  const [ticked, setTicked] = useState<Record<string, boolean>>({})
  /** Picked class ids per teacher + subject (`allocationAnswerKey`). */
  const [allocationAnswers, setAllocationAnswers] = useState<Record<string, number[]>>({})
  /** `proposalReview`: the proposed subjects whose picker the user opened (Edit), by answer key. */
  const [editingAllocations, setEditingAllocations] = useState<Set<string>>(() => new Set())
  /** The new teachers ticked to save, by `key`. */
  const [savedTeachers, setSavedTeachers] = useState<Set<string>>(() => new Set())
  /** "Save … only" rather than "Also choose …" (the default), when `peopleOnly` is offered. */
  const [onlyPeople, setOnlyPeople] = useState(false)
  // Each time the dialog opens it starts on "Also choose …": it only opens when there are real
  // class questions, so hiding them by default would make the user pick the questions back.
  useEffect(() => {
    if (show) setOnlyPeople(false)
  }, [show])
  /** The class and subject questions are left out: "Save … only" is chosen. */
  const peopleOnlyChosen = Boolean(peopleOnly) && onlyPeople
  /** The file open in the preview dialog, over this one. */
  const [previewing, setPreviewing] = useState<{file: File; sheet?: string} | null>(null)

  /** The files a column appears in that can be previewed, each once, with the sheet it is on. */
  const filesOf = (
    places: ReadonlyArray<{fileName?: string; sheet?: string}>
  ): Array<{file: File; sheet?: string}> => {
    const out: Array<{file: File; sheet?: string}> = []
    ;(places.length > 0 ? places : [{}]).forEach((place: {fileName?: string; sheet?: string}) => {
      const file = findUploadedFile(files, [place.fileName])
      if (!file || !canPreviewFile(file) || out.some((entry) => entry.file === file)) return
      out.push({file, sheet: place.sheet})
    })
    return out
  }

  // A new set of column questions starts from their guesses.
  useEffect(() => {
    const initial: DataUploadColumnAssignments = {}
    ;(columns?.questions ?? []).forEach((question) => {
      const guess = guessOf(question)
      if (guess) initial[question.header] = guess
    })
    setColumnAnswers(initial)
  }, [columns])

  /**
   * New branch questions start from the earlier answers, if any, else with nothing picked. Keyed
   * by the sheets asked about, so a caller that hands over an equal list again does not wipe what
   * was picked.
   */
  const branchKeys = branches.map((question) => question.key).join('|')
  useEffect(() => {
    setBranchAnswers(
      Object.fromEntries(
        branches
          .filter((question) => question.key in (initialBranches ?? {}))
          .map((question) => [question.key, initialBranches![question.key]])
      )
    )
    // Reset per set of questions (and each time it opens), not on every new `branches` array.
  }, [branchKeys, show])

  // A new set of teachers starts from the file: a subject the server says which of its options
  // the file names (`picked`: "both sections" is A and B) has exactly those ticked; without that,
  // one whose classes the file itself names (`fromFile`) has them all, and one the file says
  // nothing usable about starts empty.
  const allocationKeys = allocations.map((question) => question.key).join('|')
  useEffect(() => {
    const initial: Record<string, number[]> = {}
    allocations.forEach((question) =>
      question.subjects.forEach((entry) => {
        if (!entry.subject || entry.options.length === 0) return
        const offered = entry.options.map((option) => option.classId)
        // Only classes the dialog shows can start ticked.
        const ticked = entry.picked
          ? offered.filter((classId) => entry.picked!.includes(classId))
          : entry.fromFile
            ? offered
            : []
        if (ticked.length === 0) return
        initial[
          allocationAnswerKey({
            teacher: question.teacher,
            employeeId: question.employeeId,
            subject: entry.subject,
          })
        ] = ticked
      })
    )
    setAllocationAnswers(initial)
    setEditingAllocations(new Set())
    // Reset per set of questions (and each time it opens), not on every new `allocations` array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationKeys, show])

  // New teachers start ticked, as in NewTeachersModal (a new list, or the dialog reopening).
  const newTeacherKeys = JSON.stringify(newTeachers.map((teacher) => teacher.key))
  useEffect(() => {
    if (show) setSavedTeachers(new Set(JSON.parse(newTeacherKeys) as string[]))
  }, [newTeacherKeys, show])
  const toggleTeacher = (key: string) =>
    setSavedTeachers((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  /** Each asked teacher + subject (a subject the school has), with its answer key. */
  const allocationPairs = allocations.flatMap((question) =>
    question.subjects
      .filter((entry) => entry.subject)
      .map((entry) => ({
        question,
        subject: entry.subject as string,
        key: allocationAnswerKey({
          teacher: question.teacher,
          employeeId: question.employeeId,
          subject: entry.subject as string,
        }),
      }))
  )

  // Tick the confident proposals when a new set arrives.
  useEffect(() => {
    const confident = new Set(
      defaultConfirmedSubjectMappings(subjects).map((mapping) => mapping.written)
    )
    setTicked(
      Object.fromEntries(
        (subjects?.suggestions ?? []).map((mapping) => [
          mapping.written,
          confident.has(mapping.written),
        ])
      )
    )
  }, [subjects])

  const columnOptions = useMemo<FilterSelectOption[]>(
    () => [
      {value: '', label: intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.CHOOSE'})},
      ...(columns?.choices ?? []).map((choice) => ({value: choice.key, label: choice.title})),
    ],
    [columns, intl]
  )

  const columnQuestions = columns?.questions ?? []
  const suggestions = subjects?.suggestions ?? []
  const applied = subjects?.applied ?? []
  const unresolved = subjects?.unresolved ?? []
  const confirmedSubjects = suggestions.filter((mapping) => ticked[mapping.written])

  const columnsAnswered = columnQuestions.every((question) =>
    Boolean(columnAnswers[question.header])
  )
  const branchesAnswered = branches.every((question) => question.key in branchAnswers)
  /** Something to send: a column or branch answer, or a ticked subject. */
  const hasAnswers =
    Boolean(peopleOnly) ||
    columnQuestions.length > 0 ||
    branches.length > 0 ||
    confirmedSubjects.length > 0 ||
    allocationPairs.length > 0 ||
    newTeachers.length > 0
  const canConfirm = columnsAnswered && branchesAnswered && hasAnswers

  /** Several files in one upload: the file name is only worth showing when they differ. */
  const showFileNames = new Set(branches.map((question) => question.fileName ?? '')).size > 1
  /**
   * The branch section's title and hint are shared by every card: the cards' noun when they all
   * have the same one, a neutral wording when they differ (a teachers sheet and an allocation).
   */
  const branchNouns = new Set(branches.map(nounOf))
  // Every question about a whole file (one per file, "<fileName>::"): said about files.
  const branchHeading = branches.length > 0 && branches.every(isFileBranchQuestion)
    ? {title: 'DATA_UPLOAD.BRANCHES.TITLE_FILES', hint: 'DATA_UPLOAD.BRANCHES.HINT_FILES'}
    : branchNouns.size === 1
      ? BRANCH_WORDING[[...branchNouns][0]]
      : {title: 'DATA_UPLOAD.BRANCHES.TITLE_NEUTRAL', hint: 'DATA_UPLOAD.BRANCHES.HINT_NEUTRAL'}

  const previewButton = (file: File, sheet: string | undefined, suffix = '') => (
    <button
      key={`${file.name}-${sheet ?? ''}`}
      type='button'
      className='btn btn-sm btn-light-primary border border-primary ms-2 d-inline-flex align-items-center gap-1'
      style={{padding: '0.25rem 0.75rem', fontSize: '0.8rem'}}
      onClick={() => setPreviewing({file, sheet})}
      title={file.name}
    >
      <Eye size={14} aria-hidden />
      {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.PREVIEW'})}
      {suffix}
    </button>
  )

  /**
   * The files these questions are about: only the ones the questions name (a column's file, a
   * sheet's file, a teacher's lines), never every file of the read — the others are read and
   * ready. When none says, the one file uploaded (every question is about it), else none.
   */
  const askedFileNames = (() => {
    const named = new Set(
      [
        ...columnQuestions.flatMap((question) => question.appearsIn.map((place) => place.fileName)),
        ...branches.map((question) => question.fileName),
        ...allocations.flatMap((question) => question.fileNames),
      ]
        .map((name) => String(name ?? '').trim())
        .filter(Boolean)
    )
    if (named.size > 0) return Array.from(named)
    return files.length === 1 ? [files[0].name] : []
  })()

  const skip = () =>
    onSkip({
      columns: Object.fromEntries(
        columnQuestions.map((question) => [question.header, IGNORE_COLUMN])
      ),
      branches: branches.map((question) => question.key),
      subjects: suggestions.map((mapping) => mapping.written),
      allocations: skippedAllocationAnswers(allocations),
    })

  /**
   * "ICT read as Computing": the subject's label when the file wrote it under another name
   * (`fileSubject`), else null.
   */
  const readAs = (entry: DataUploadAllocationQuestion['subjects'][number]): string | null =>
    // The server's own sentence ("file says X, read as Y") when it sends one.
    entry.subject && entry.issue === 'SUBJECT_READ_AS' && entry.issueMessage
      ? entry.issueMessage
      : entry.subject &&
          entry.fileSubject &&
          entry.fileSubject.trim().toLowerCase() !== entry.subject.trim().toLowerCase()
        ? intl.formatMessage(
            {id: 'DATA_UPLOAD.ALLOCATIONS.READ_AS'},
            {written: entry.fileSubject, subject: entry.subject}
          )
        : null

  /**
   * `proposalReview`: one subject the file proposed classes for. The ticked classes as chips with
   * how the file was read under them, and Edit to open the picker (pre-ticked with the proposal);
   * Done closes it again. The reading goes once the ticks no longer match what it describes.
   */
  const renderProposedSubject = (
    question: DataUploadAllocationQuestion,
    entry: DataUploadAllocationQuestion['subjects'][number],
    key: string
  ) => {
    const value = allocationAnswers[key] ?? []
    const editing = editingAllocations.has(key)
    const names = entry.options
      .filter((option) => value.includes(option.classId))
      .map((option) => option.displayName)
    const toggleEdit = () =>
      setEditingAllocations((prev) => {
        const next = new Set(prev)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      })
    return (
      <div key={`${question.key}-${entry.written}`} className='mb-3'>
        <div className='d-flex align-items-center justify-content-between gap-2'>
          <div className='sheet-assign-label mb-0'>
            {entry.subject}
            {readAs(entry) && (
              <span className='text-muted fw-normal'>
                {' · '}
                {readAs(entry)}
              </span>
            )}
          </div>
          <button
            type='button'
            className='btn btn-sm btn-light-primary flex-shrink-0'
            style={{padding: '0.2rem 0.75rem', fontSize: '0.8rem'}}
            disabled={submitting}
            aria-expanded={editing}
            onClick={toggleEdit}
          >
            {intl.formatMessage({
              id: editing ? 'DATA_UPLOAD.ALLOCATIONS.DONE' : 'DATA_UPLOAD.ALLOCATIONS.EDIT',
            })}
          </button>
        </div>
        {editing ? (
          <div className='mt-2'>
            <ClassGroupPicker
              options={entry.options}
              value={value}
              disabled={submitting}
              onChange={(classIds) => setAllocationAnswers((prev) => ({...prev, [key]: classIds}))}
            />
          </div>
        ) : (
          <div className='d-flex flex-wrap gap-1 mt-2'>
            {names.length > 0 ? (
              names.map((name) => (
                <span key={name} className='sheet-assign-chip'>
                  {name}
                </span>
              ))
            ) : (
              <span className='text-muted fs-7'>
                {intl.formatMessage({id: 'DATA_UPLOAD.ALLOCATIONS.NO_CLASSES'})}
              </span>
            )}
          </div>
        )}
        {/* How the file was read ("Left unticked, as the file gives…"): also while editing, so
            it stays clear why those classes were not ticked (QA 554). */}
        {entry.reading && (editing || sameClassIds(value, proposedClassIds(entry))) && (
          <div className='text-muted fs-7 mt-1'>{entry.reading}</div>
        )}
      </div>
    )
  }

  /** One teacher's "Which classes?" card: each subject with the classes to pick. */
  const renderAllocationQuestion = (question: DataUploadAllocationQuestion) => {
    const answered = question.subjects.some((entry) => {
      if (!entry.subject) return false
      const key = allocationAnswerKey({
        teacher: question.teacher,
        employeeId: question.employeeId,
        subject: entry.subject,
      })
      return (allocationAnswers[key] ?? []).length > 0
    })
    return (
      <div
        key={question.key}
        className={`sheet-assign-card${answered ? ' is-answered' : ''}`}
      >
        <div className='d-flex align-items-center gap-2 mb-3'>
          <UserRound size={16} className='text-muted flex-shrink-0' aria-hidden />
          <span className='sheet-assign-name'>{question.teacher}</span>
          {question.employeeId && (
            <span className='sheet-assign-chip'>{question.employeeId}</span>
          )}
        </div>
        {question.subjects.map((entry) => {
          const key = entry.subject
            ? allocationAnswerKey({
                teacher: question.teacher,
                employeeId: question.employeeId,
                subject: entry.subject,
              })
            : ''
          if (proposalReview && entry.subject && proposedClassIds(entry).length > 0) {
            return renderProposedSubject(question, entry, key)
          }
          // A line with an issue and nothing picked: the server's sentence as it is, with the
          // picker already open under it (the picker is the edit; its options are narrowed).
          // (Review mode already says a "read as" sentence as the label.)
          const unresolvedMessage =
            entry.subject &&
            entry.issueMessage &&
            !(proposalReview && readAs(entry) === entry.issueMessage)
              ? entry.issueMessage
              : null
          return (
            <div key={`${question.key}-${entry.written}`} className='mb-3'>
              <div className='sheet-assign-label'>
                {proposalReview && readAs(entry)
                  ? readAs(entry)
                  : entry.subject && entry.subject !== entry.written
                    ? `${entry.written} → ${entry.subject}`
                    : entry.written}
                {/* What the file wrote: classes it names (picked below), classes that
                    match none of the school's, or nothing at all. */}
                {entry.subject && entry.issueMessage ? null : entry.subject && (
                  <span className='text-muted fw-normal'>
                    {', '}
                    {entry.fromFile
                      ? intl.formatMessage(
                          {id: 'DATA_UPLOAD.ALLOCATIONS.FILE_SAYS'},
                          {
                            classes: (entry.classes.length > 0
                              ? entry.classes
                              : entry.options.map((option) => option.displayName)
                            ).join(', '),
                          }
                        )
                      : entry.classes.length > 0
                        ? intl.formatMessage(
                            {id: 'DATA_UPLOAD.ALLOCATIONS.FILE_SAYS_NO_MATCH'},
                            {classes: entry.classes.join(', ')}
                          )
                        : intl.formatMessage({id: 'DATA_UPLOAD.ALLOCATIONS.NOT_SAID'})}
                  </span>
                )}
              </div>
              {unresolvedMessage && (
                <div className={`fs-7 mb-2 ${entry.pending ? 'text-warning' : 'text-muted'}`}>
                  {unresolvedMessage}
                </div>
              )}
              {/* Confirmed earlier with no classes and still waiting, with no sentence of its own. */}
              {entry.subject && entry.pending && !unresolvedMessage && (
                <div className='fs-7 mb-2 text-warning'>
                  {intl.formatMessage({id: 'DATA_UPLOAD.ALLOCATIONS.PENDING_HINT'})}
                </div>
              )}
              {entry.subject ? (
                <ClassGroupPicker
                  options={entry.options}
                  value={allocationAnswers[key] ?? []}
                  disabled={submitting}
                  onChange={(classIds) =>
                    setAllocationAnswers((prev) => ({...prev, [key]: classIds}))
                  }
                />
              ) : (
                // A subject the school doesn't have: flagged, not hidden (nothing to pick).
                <div className='sheet-assign-flag'>
                  <AlertTriangle size={14} aria-hidden className='flex-shrink-0' />
                  <span>
                    {entry.issueMessage ||
                      intl.formatMessage({id: 'DATA_UPLOAD.ALLOCATIONS.NO_SUBJECT'})}
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  /**
   * The "Which classes?" questions by the file their lines are in (`fileNames`), in the order they
   * came: only the files that have questions, each under its name — the others are read and ready.
   * Questions that name no file (one file, older servers) stay as one list with no heading.
   */
  const allocationGroups = (() => {
    const groups: Array<{fileName: string; questions: DataUploadAllocationQuestion[]}> = []
    const named = allocations.some((question) => question.fileNames.length > 0)
    allocations.forEach((question) => {
      const fileName = named ? (question.fileNames[0] ?? '') : ''
      const group = groups.find((entry) => entry.fileName === fileName)
      if (group) group.questions.push(question)
      else groups.push({fileName, questions: [question]})
    })
    return groups
  })()

  /** What a file's questions are about, by kind: "2 lines name no class · 1 subject isn't…". */
  const issuesOf = (questions: readonly DataUploadAllocationQuestion[]): string => {
    const counts = new Map<string, number>()
    questions.forEach((question) =>
      question.subjects.forEach((entry) => {
        // Review mode: a proposed line is not an issue any more, only the unresolved ones are.
        if (proposalReview && entry.subject && proposedClassIds(entry).length > 0) return
        const issue = entry.issue ?? (entry.subject ? '' : 'SUBJECT_NOT_IN_SCHOOL')
        if (issue in ALLOCATION_ISSUE_KEYS) counts.set(issue, (counts.get(issue) ?? 0) + 1)
      })
    )
    return Array.from(counts.entries())
      .map(([issue, count]) =>
        intl.formatMessage({id: ALLOCATION_ISSUE_KEYS[issue as AllocationIssueKey]}, {count})
      )
      .join(' · ')
  }

  /**
   * The lines by what they need, counted apart: "3 lines need classes chosen, 1 line can't be
   * assigned". A line whose classes start picked (review mode) needs nothing chosen.
   */
  const allocationCountsText = (() => {
    let choose = 0
    let review = 0
    let unassignable = 0
    allocations.forEach((question) =>
      question.subjects.forEach((entry) => {
        if (!entry.subject) unassignable += 1
        else if (entry.pending) review += 1
        else if (!(proposalReview && proposedClassIds(entry).length > 0)) choose += 1
      })
    )
    // Only worth saying when the lines need different things.
    if (review === 0 && unassignable === 0) return null
    return [
      choose > 0
        ? intl.formatMessage({id: 'DATA_UPLOAD.ALLOCATIONS.COUNT_CHOOSE'}, {count: choose})
        : null,
      review > 0
        ? intl.formatMessage({id: 'DATA_UPLOAD.ALLOCATIONS.COUNT_REVIEW'}, {count: review})
        : null,
      unassignable > 0
        ? intl.formatMessage({id: 'DATA_UPLOAD.QUESTIONS.LINES_CANT_BE_ASSIGNED'}, {count: unassignable})
        : null,
    ]
      .filter(Boolean)
      .join(', ')
  })()

  const sectionTitle = (icon: React.ReactNode, id: string, hint?: string) => (
    <div className='mb-3'>
      <div className='d-flex align-items-center gap-2 fw-bold fs-5'>
        {icon}
        {intl.formatMessage({id})}
      </div>
      {hint && <p className='sheet-assign-hint mt-1 mb-0'>{intl.formatMessage({id: hint})}</p>}
    </div>
  )

  return (
    <>
      <Modal
        show={show}
        onHide={submitting ? undefined : (onClose ?? skip)}
        centered
        size='lg'
        backdrop={submitting ? 'static' : undefined}
      >
        <Modal.Header closeButton={!submitting} className='border-0 pb-0'>
          <div style={{minWidth: 0}}>
            <Modal.Title className='fs-3'>
              {intl.formatMessage({id: 'DATA_UPLOAD.QUESTIONS.TITLE'})}
            </Modal.Title>
            {/* Which upload is being asked about: several can be on the card at once. */}
            {askedFileNames.length > 0 && (
              <p
                className='sheet-assign-hint mt-2 mb-0 d-flex align-items-start gap-2 text-break'
                title={askedFileNames.join(', ')}
              >
                <FileText size={15} className='flex-shrink-0 mt-1' aria-hidden />
                <span>
                  {intl.formatMessage(
                    {id: 'DATA_UPLOAD.QUESTIONS.FILES'},
                    {count: askedFileNames.length, names: askedFileNames.join(', ')}
                  )}
                </span>
              </p>
            )}
          </div>
        </Modal.Header>

        <Modal.Body className='pt-4'>
          {peopleOnly && (
            <section className='mb-6' role='radiogroup'>
              <div className='row g-3'>
                {[true, false].map((only) => {
                  const wording = PEOPLE_ONLY_WORDING[peopleOnly][only ? 'only' : 'also']
                  const key = wording.title
                  const chosen = onlyPeople === only
                  return (
                    <div key={key} className='col-sm-6'>
                      <button
                        type='button'
                        role='radio'
                        aria-checked={chosen}
                        disabled={submitting}
                        className={`sheet-assign-card w-100 h-100 mb-0 text-start${
                          chosen ? ' is-answered' : ''
                        }`}
                        onClick={() => setOnlyPeople(only)}
                      >
                        <span className='d-flex align-items-center gap-2'>
                          {chosen ? (
                            <Check size={16} className='text-primary flex-shrink-0' aria-hidden />
                          ) : only ? (
                            <UserRound size={16} className='text-muted flex-shrink-0' aria-hidden />
                          ) : (
                            <Building2 size={16} className='text-muted flex-shrink-0' aria-hidden />
                          )}
                          <span className='sheet-assign-name'>
                            {intl.formatMessage({id: wording.title})}
                          </span>
                        </span>
                        <span className='d-block sheet-assign-hint mt-1'>
                          {intl.formatMessage({id: wording.hint})}
                        </span>
                      </button>
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {columnQuestions.length > 0 && (
            <section className='mb-6'>
              {sectionTitle(
                <Columns3 size={18} className='text-primary' aria-hidden />,
                'DATA_UPLOAD.COLUMNS.TITLE',
                'DATA_UPLOAD.COLUMNS.HINT'
              )}
              {columnQuestions.map((question) => {
                const chosen = columnAnswers[question.header] ?? ''
                const places = question.appearsIn
                  .map((place) =>
                    [place.fileName, place.sheet, place.column].filter(Boolean).join(' · ')
                  )
                  .filter(Boolean)
                return (
                  <div
                    key={question.key}
                    className={`sheet-assign-card${chosen ? ' is-answered' : ''}`}
                  >
                    <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
                      <div className='flex-grow-1' style={{minWidth: 0}}>
                        <div className='d-flex align-items-center gap-2'>
                          <Columns3 size={16} className='text-muted flex-shrink-0' aria-hidden />
                          <span className='sheet-assign-name'>{question.header}</span>
                          {filesOf(question.appearsIn).map(({file, sheet}, _index, all) =>
                            previewButton(file, sheet, all.length > 1 ? ` · ${file.name}` : '')
                          )}
                        </div>
                        {places.length > 1 && (
                          <div className='sheet-assign-file ms-6'>
                            {intl.formatMessage(
                              {id: 'DATA_UPLOAD.COLUMNS.APPEARS_IN'},
                              {places: places.join(', ')}
                            )}
                          </div>
                        )}
                        {question.samples.length > 0 && (
                          <div className='d-flex flex-wrap gap-2 mt-2'>
                            {question.samples.map((sample, index) => (
                              <span
                                key={`${question.key}-s-${index}`}
                                className='sheet-assign-chip'
                                title={sample}
                              >
                                {sample}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className='sheet-assign-field'>
                        <label
                          className='sheet-assign-label'
                          htmlFor={`${fieldId}-col-${question.key}`}
                        >
                          {intl.formatMessage({id: 'DATA_UPLOAD.COLUMNS.FIELD'})}
                        </label>
                        <FilterSelect
                          id={`${fieldId}-col-${question.key}`}
                          value={chosen}
                          options={columnOptions}
                          onChange={(value) =>
                            setColumnAnswers((prev) => ({...prev, [question.header]: value}))
                          }
                          disabled={submitting}
                          // As in SheetAssignmentModal: an absolute menu inside the dialog.
                          placement='under'
                          className='w-100'
                          triggerClassName='filter-select-solid w-100'
                          ariaLabel={intl.formatMessage({id: 'DATA_UPLOAD.COLUMNS.FIELD'})}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </section>
          )}

          {branches.length > 0 && (
            <section className='mb-6'>
              {sectionTitle(
                <Building2 size={18} className='text-primary' aria-hidden />,
                branchHeading.title,
                branchHeading.hint
              )}
              {branches.map((question) => {
                const chosen = branchAnswers[question.key]
                const answered = chosen !== undefined
                // A photo or PDF has no sheet: its file name is its title, not "file.png::". A
                // question about a whole file reads "Which branch is Model_Town.xlsx for?".
                const fileName = question.fileName || question.key.replace(/::$/, '')
                const title = isFileBranchQuestion(question)
                  ? intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.FILE_QUESTION'}, {file: fileName})
                  : question.sheet || question.fileName || question.key
                const file = findUploadedFile(files, [question.fileName, question.sheet])
                const preview = file && canPreviewFile(file) ? file : null
                // The same dropdown as the other questions: the branches, then "Both".
                const choices: FilterSelectOption[] = [
                  {value: '', label: intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.CHOOSE'})},
                  ...question.branches.map((branch) => ({
                    value: String(branch.branchId),
                    label: branch.branch || String(branch.branchId),
                  })),
                  {
                    value: MIXED_BRANCH,
                    label: intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.MIXED'}),
                  },
                ]
                const wording = intl.formatMessage({id: BRANCH_WORDING[nounOf(question)].question})
                return (
                  <div
                    key={question.key}
                    className={`sheet-assign-card${answered ? ' is-answered' : ''}`}
                  >
                    <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
                      <div className='flex-grow-1' style={{minWidth: 0}}>
                        <div className='d-flex align-items-center gap-2'>
                          {answered ? (
                            <Check size={16} className='text-primary flex-shrink-0' aria-hidden />
                          ) : (
                            <Building2 size={16} className='text-muted flex-shrink-0' aria-hidden />
                          )}
                          <span className='sheet-assign-name'>{title}</span>
                          {preview && previewButton(preview, question.sheet)}
                        </div>
                        {showFileNames &&
                          !isFileBranchQuestion(question) &&
                          question.fileName &&
                          question.fileName !== title && (
                          <div className='sheet-assign-file ms-6'>{question.fileName}</div>
                        )}
                        {question.rows != null && (
                          <div className='d-flex flex-wrap gap-2 mt-2'>
                            <span className='sheet-assign-chip sheet-assign-chip-count'>
                              {intl.formatMessage(
                                {id: BRANCH_WORDING[nounOf(question)].rows},
                                {count: question.rows}
                              )}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className='sheet-assign-field'>
                        <label
                          className='sheet-assign-label'
                          htmlFor={`${fieldId}-branch-${question.key}`}
                        >
                          {wording}
                        </label>
                        <FilterSelect
                          id={`${fieldId}-branch-${question.key}`}
                          value={answered ? String(chosen) : ''}
                          options={choices}
                          onChange={(value) => {
                            if (!value) return
                            setBranchAnswers((prev) => ({
                              ...prev,
                              [question.key]: value === MIXED_BRANCH ? MIXED_BRANCH : Number(value),
                            }))
                          }}
                          disabled={submitting}
                          // As in SheetAssignmentModal: an absolute menu inside the dialog.
                          placement='under'
                          className='w-100'
                          triggerClassName='filter-select-solid w-100'
                          ariaLabel={wording}
                        />
                        {/* An allocation sheet without a Branch column: "Both" adds each line
                            that names no branch to its class in every branch. */}
                        {nounOf(question) === 'allocation' && (
                          <div className='sheet-assign-file mt-2'>
                            {intl.formatMessage({
                              id: 'DATA_UPLOAD.BRANCHES.MIXED_ALLOCATION_HINT',
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {question.sample.length > 0 && (
                      <div className='sheet-assign-sample'>
                        <div className='sheet-assign-sample-caption'>
                          {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.SAMPLE'})}
                        </div>
                        <div className='table-responsive'>
                          <table className='sheet-assign-table'>
                            <thead>
                              <tr>
                                <th>{intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.NAME'})}</th>
                                <th>{intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.CLASS'})}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {question.sample
                                .slice(0, MAX_BRANCH_SAMPLE_ROWS)
                                .map((row, rowIndex) => (
                                  <tr key={`${question.key}-r-${rowIndex}`}>
                                    {row.slice(0, 2).map((cell, cellIndex) => (
                                      <td
                                        key={`${question.key}-r-${rowIndex}-c-${cellIndex}`}
                                        title={cell}
                                      >
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </section>
          )}

          {allocations.length > 0 && !peopleOnlyChosen && (
            <section className='mb-6'>
              {/* No fixed "your file doesn't say which classes" hint: each subject says what its
                  file wrote, and most files do name the classes. */}
              {sectionTitle(
                <UserRound size={18} className='text-primary' aria-hidden />,
                'DATA_UPLOAD.ALLOCATIONS.TITLE',
                // Review mode with something proposed: say the ticks are the file's reading.
                proposalReview &&
                  allocations.some((question) =>
                    question.subjects.some(
                      (entry) => entry.subject && proposedClassIds(entry).length > 0
                    )
                  )
                  ? 'DATA_UPLOAD.ALLOCATIONS.PROPOSAL_HINT'
                  : undefined
              )}
              {allocationCountsText && (
                <p className='sheet-assign-hint mb-3' role='status'>
                  {allocationCountsText}
                </p>
              )}
              {allocationGroups.map((group) => (
                <React.Fragment key={group.fileName || '-'}>
                  {/* Several files: only the ones with questions, each under its name. */}
                  {group.fileName && (
                    <div className='sheet-assign-file-group'>
                      <FileText size={15} aria-hidden className='flex-shrink-0' />
                      <div style={{minWidth: 0}}>
                        <div>
                          {intl.formatMessage(
                            {
                              id: proposalReview
                                ? 'DATA_UPLOAD.ALLOCATIONS.FILE_GROUP_REVIEW'
                                : 'DATA_UPLOAD.ALLOCATIONS.FILE_GROUP',
                            },
                            {file: group.fileName}
                          )}
                        </div>
                        {issuesOf(group.questions) && (
                          <div className='sheet-assign-file-issues'>
                            {issuesOf(group.questions)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {group.questions.map(renderAllocationQuestion)}
                </React.Fragment>
              ))}
            </section>
          )}

          {newTeachers.length > 0 && (
            <section className='mb-6'>
              {sectionTitle(
                <UserPlus size={18} className='text-primary' aria-hidden />,
                'DATA_UPLOAD.NEW_TEACHERS.TITLE',
                'DATA_UPLOAD.NEW_TEACHERS.HINT_QUESTIONS'
              )}
              {newTeachers.map((teacher) => {
                const checked = savedTeachers.has(teacher.key)
                const inputId = `${fieldId}-teacher-${teacher.key}`
                return (
                  <div
                    key={teacher.key}
                    className={`sheet-assign-card${checked ? ' is-answered' : ''}`}
                  >
                    <div className='d-flex align-items-start gap-3'>
                      <input
                        id={inputId}
                        type='checkbox'
                        className='form-check-input mt-1 flex-shrink-0'
                        checked={checked}
                        disabled={submitting}
                        onChange={() => toggleTeacher(teacher.key)}
                      />
                      <label htmlFor={inputId} className='flex-grow-1' style={{minWidth: 0}}>
                        <span className='d-flex flex-wrap align-items-center gap-2'>
                          <span className='sheet-assign-name'>{teacher.name}</span>
                          {teacher.employeeId && (
                            <span className='badge badge-light fs-8 fw-semibold'>
                              {teacher.employeeId}
                            </span>
                          )}
                          <span className='badge badge-light-primary fs-8 fw-semibold'>
                            {intl.formatMessage(
                              {id: 'DATA_UPLOAD.NEW_TEACHERS.LINES'},
                              {count: teacher.lines}
                            )}
                          </span>
                        </span>
                        {teacher.samples.length > 0 && (
                          <span className='d-block text-muted fs-7 mt-1'>
                            {teacher.samples.join(' · ')}
                          </span>
                        )}
                      </label>
                    </div>
                  </div>
                )
              })}
            </section>
          )}

          {(suggestions.length > 0 || unresolved.length > 0) && !peopleOnlyChosen && (
            <section>
              {sectionTitle(
                <BookOpen size={18} className='text-primary' aria-hidden />,
                'DATA_UPLOAD.SUBJECTS.TITLE',
                'DATA_UPLOAD.SUBJECTS.HINT'
              )}
              {suggestions.map((mapping) => {
                const low = (mapping.confidence ?? 0) < SUBJECT_MAPPING_CONFIRM_THRESHOLD
                const checked = Boolean(ticked[mapping.written])
                return (
                  <div
                    key={mapping.written}
                    className={`sheet-assign-card${checked ? ' is-answered' : ''}`}
                  >
                    <div className='form-check form-check-custom form-check-solid mb-0'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id={`${fieldId}-subject-${mapping.written}`}
                        disabled={submitting}
                        checked={checked}
                        onChange={(event) =>
                          setTicked((prev) => ({...prev, [mapping.written]: event.target.checked}))
                        }
                      />
                      <label
                        className='form-check-label ms-3'
                        htmlFor={`${fieldId}-subject-${mapping.written}`}
                        title={mapping.reason ?? undefined}
                      >
                        <span className='fw-semibold d-block text-break'>
                          <span className='fw-bolder'>{mapping.written}</span>
                          {' → '}
                          {mapping.subject}
                          {mapping.confidence != null && (
                            <span className={`ms-2 fs-8 ${low ? 'text-warning' : 'text-muted'}`}>
                              {'· '}
                              {percent(mapping.confidence)}
                              {low &&
                                ` · ${intl.formatMessage({id: 'DATA_UPLOAD.SUBJECTS.LOW_CONFIDENCE'})}`}
                            </span>
                          )}
                        </span>
                        {mapping.reason && (
                          <span className='text-muted fs-8'>{mapping.reason}</span>
                        )}
                      </label>
                    </div>
                  </div>
                )
              })}

              {applied.length > 0 && (
                <div className='mt-4'>
                  <div className='fs-8 fw-bold text-muted'>
                    {intl.formatMessage({id: 'DATA_UPLOAD.SUBJECTS.APPLIED_TITLE'})}
                  </div>
                  <ul className='mb-0 mt-1 ps-4 fs-8 text-muted'>
                    {applied.map((mapping) => (
                      <li key={`applied-${mapping.written}`} className='text-break'>
                        {intl.formatMessage(
                          {id: 'DATA_UPLOAD.SUBJECTS.MAPPING'},
                          {written: mapping.written, subject: mapping.subject}
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {unresolved.length > 0 && (
                <div className='d-flex align-items-start gap-2 mt-4 fs-7' role='note'>
                  <Info size={16} className='text-muted flex-shrink-0 mt-1' aria-hidden />
                  <span className='text-break'>
                    {intl.formatMessage(
                      {id: 'DATA_UPLOAD.SUBJECTS.NOT_FOUND'},
                      {names: unresolved.join(', ')}
                    )}
                  </span>
                </div>
              )}
            </section>
          )}
        </Modal.Body>

        <Modal.Footer className='border-0 d-flex justify-content-end gap-2'>
          <button type='button' className='btn btn-light' disabled={submitting} onClick={skip}>
            {intl.formatMessage({id: 'DATA_UPLOAD.COLUMNS.SKIP'})}
          </button>
          <button
            type='button'
            className='btn btn-primary d-inline-flex align-items-center gap-2'
            disabled={!canConfirm || submitting}
            onClick={() =>
              onConfirm({
                columns: Object.fromEntries(
                  Object.entries(columnAnswers).filter(([, field]) => Boolean(field))
                ),
                branches: Object.fromEntries(
                  branches
                    .filter((question) => question.key in branchAnswers)
                    .map((question) => [question.key, branchAnswers[question.key]])
                ),
                subjects: peopleOnlyChosen ? [] : confirmedSubjects,
                allocations: (peopleOnlyChosen ? [] : allocationPairs).map(
                  ({question, subject, key}) => ({
                    teacher: question.teacher,
                    employeeId: question.employeeId,
                    subject,
                    classIds: allocationAnswers[key] ?? [],
                  })
                ),
                newTeachers: newTeachers
                  .map((teacher) => teacher.key)
                  .filter((key) => savedTeachers.has(key)),
                peopleOnly: peopleOnlyChosen,
              })
            }
          >
            {submitting && (
              <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
            )}
            {intl.formatMessage({
              id: submitting
                ? 'DATA_UPLOAD.SHEETS.READING'
                : proposalReview && allocationPairs.length > 0 && !peopleOnlyChosen
                  ? 'DATA_UPLOAD.ALLOCATIONS.CONFIRM_ASSIGNMENTS'
                  : 'DATA_UPLOAD.COLUMNS.CONFIRM',
            })}
          </button>
        </Modal.Footer>
      </Modal>
      {/* Over this dialog: the file a column or sheet is in, to see what it holds. */}
      <FilePreviewModal
        file={previewing?.file ?? null}
        sheet={previewing?.sheet}
        onClose={() => setPreviewing(null)}
      />
    </>
  )
}

export default ColumnAssignmentModal
